import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { UnknownError } from '@atlassian/jira-polaris-lib-errors/src/common/utils/unknown-error/index.tsx';
import { FILTER_INTERVAL_SPOTLIGHT_DISMISSED } from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';

export const loadFilterIntervalSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().filterInterval.spotlightDismissed === 'boolean') {
			return;
		}

		try {
			const spotlightDismissed = await getUserProperty<boolean>(
				accountId,
				FILTER_INTERVAL_SPOTLIGHT_DISMISSED,
			);

			setState({
				filterInterval: {
					spotlightDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				setState({
					filterInterval: {
						spotlightDismissed: false,
					},
				});

				return;
			}

			const errorMessage = `Failed to get user property with key: ${FILTER_INTERVAL_SPOTLIGHT_DISMISSED}`;
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.filter-interval-spotlight',
				errorMessage,
				error instanceof Error ? error : new UnknownError(errorMessage, error),
			);
		}
	};

export const setFilterIntervalSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		setState({
			filterInterval: {
				spotlightDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, FILTER_INTERVAL_SPOTLIGHT_DISMISSED, 'true');
		} catch (error) {
			const errorMessage = `Failed to set user property with key: ${FILTER_INTERVAL_SPOTLIGHT_DISMISSED}`;
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.filter-interval-spotlight',
				errorMessage,
				error instanceof Error ? error : new UnknownError(errorMessage, error),
			);
		}
	};
